import { Fragment, useId, type ReactNode } from 'react';

export interface MetaStripSegment {
  /** Stable identity of the fact; also the React key of the segment. */
  id: string;
  /** Names the fact for assistive technology — the strip shows no caption. */
  label: string;
  /** The fact itself. A nullish or empty value drops the whole segment. */
  value: ReactNode;
}

export interface MetaStripProps {
  segments: readonly MetaStripSegment[];
  /** Accessible name of the strip as a whole. */
  ariaLabel: string;
}

/**
 * @aiPurpose Compact horizontal metadata line: the surviving facts joined by
 *   the house middot, one tone fainter than the facts themselves. Values carry
 *   no visible caption, so each segment states its fact to assistive
 *   technology through the hidden label it is named by.
 * @aiInvariant A segment with no value is dropped together with its
 *   separator — a strip never opens, ends or breaks on a dangling middot.
 */
export function MetaStrip({ segments, ariaLabel }: MetaStripProps) {
  const scope = useId();
  const shown = segments.filter(
    (segment) => segment.value !== null && segment.value !== undefined && segment.value !== '',
  );
  if (shown.length === 0) return null;
  return (
    <div
      role="list"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        minWidth: 0,
        gap: 'var(--s-2)',
        fontSize: 'var(--fz-12)',
        color: 'var(--fg-3)',
      }}
    >
      {shown.map((segment, index) => (
        <Fragment key={segment.id}>
          {index > 0 && (
            <span aria-hidden="true" style={{ color: 'var(--fg-4)' }}>
              ·
            </span>
          )}
          <span
            role="listitem"
            aria-labelledby={`${scope}${segment.id}-label ${scope}${segment.id}-value`}
          >
            {/* WHY: `hidden` keeps the caption out of the layout while
                `aria-labelledby` still reads it, so the value is announced as
                the fact it is instead of as a bare string. */}
            <span id={`${scope}${segment.id}-label`} hidden>
              {segment.label}
            </span>
            <span id={`${scope}${segment.id}-value`}>{segment.value}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}
